import React from 'react';
import { Phone, Mail, MapPin, Clock, MessageCircle } from 'lucide-react';
import Layout from '../components/layout/Layout';
import SEO from '../components/seo/SEO';

const PHONE_DISPLAY = '+90 (505) 869 58 34';
const PHONE_TEL = '+905058695834';
const EMAIL = 'info@haberturkiyeden.com';
const ADDRESS = 'Esentepe, Bahar sk. No:13 River Plaza Şişli/İstanbul';

const ContactCard = ({ Icon, label, primary, secondary, href, testId }) => {
  const content = (
    <div className="bg-white rounded-xl border border-gray-200 p-6 h-full transition-all hover:border-red-300 hover:shadow-lg group">
      <div className="w-12 h-12 rounded-lg bg-red-50 group-hover:bg-red-600 flex items-center justify-center mb-4 transition-colors">
        <Icon className="w-6 h-6 text-red-600 group-hover:text-white transition-colors" />
      </div>
      <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">{label}</p>
      <p className="text-base md:text-lg font-semibold text-[#0F172A] leading-snug break-words">{primary}</p>
      {secondary && <p className="text-sm text-gray-500 mt-1">{secondary}</p>}
    </div>
  );
  if (href) {
    return (
      <a href={href} data-testid={testId} className="block h-full focus:outline-none focus:ring-2 focus:ring-red-500 rounded-xl">
        {content}
      </a>
    );
  }
  return <div data-testid={testId}>{content}</div>;
};

const ContactPage = () => {
  return (
    <Layout>
      <SEO
        title="İletişim"
        description={`Haber Türkiye'den iletişim bilgileri. Telefon: ${PHONE_DISPLAY}, E-posta: ${EMAIL}, Adres: ${ADDRESS}`}
        keywords="iletişim, contact, telefon, e-posta, adres, haber türkiye"
      />
      <div className="bg-gray-50 min-h-screen" data-testid="contact-page">
        {/* Hero */}
        <section className="bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#1E3A8A] text-white">
          <div className="max-w-5xl mx-auto px-4 md:px-6 py-14 md:py-20">
            <div className="flex items-center gap-2 text-red-400 text-xs font-bold uppercase tracking-[0.2em] mb-3">
              <MessageCircle className="w-4 h-4" />
              Bize Ulaşın
            </div>
            <h1 className="text-3xl md:text-5xl font-black tracking-tight mb-4">İletişim</h1>
            <p className="text-gray-300 text-base md:text-lg max-w-2xl leading-relaxed">
              Haber önerileri, basın iletişimi ve genel sorularınız için bize aşağıdaki kanallardan
              ulaşabilirsiniz. En kısa sürede dönüş yapıyoruz.
            </p>
          </div>
        </section>

        {/* Contact Cards */}
        <section className="max-w-5xl mx-auto px-4 md:px-6 -mt-10 md:-mt-14 pb-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
            <ContactCard
              Icon={Phone}
              label="Telefon"
              primary={PHONE_DISPLAY}
              secondary="Hafta içi 09:00 – 18:00"
              href={`tel:${PHONE_TEL}`}
              testId="contact-phone"
            />
            <ContactCard
              Icon={Mail}
              label="E-posta"
              primary={EMAIL}
              secondary="24 saat içinde yanıtlıyoruz"
              href={`mailto:${EMAIL}`}
              testId="contact-email"
            />
            <ContactCard
              Icon={MapPin}
              label="Adres"
              primary={ADDRESS}
              secondary="Şişli / İstanbul"
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(ADDRESS)}`}
              testId="contact-address"
            />
          </div>
        </section>

        {/* Office Info + Map */}
        <section className="max-w-5xl mx-auto px-4 md:px-6 pb-16">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            {/* Info column */}
            <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 p-6 md:p-8">
              <h2 className="text-xl font-bold text-[#0F172A] mb-4">Ofisimiz</h2>
              <div className="space-y-4 text-sm">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-gray-700">Adres</p>
                    <p className="text-gray-600 leading-relaxed">{ADDRESS}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Phone className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-gray-700">Telefon</p>
                    <a
                      href={`tel:${PHONE_TEL}`}
                      className="text-gray-600 hover:text-red-600 transition-colors"
                      data-testid="contact-phone-inline"
                    >
                      {PHONE_DISPLAY}
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-gray-700">E-posta</p>
                    <a
                      href={`mailto:${EMAIL}`}
                      className="text-gray-600 hover:text-red-600 transition-colors break-all"
                      data-testid="contact-email-inline"
                    >
                      {EMAIL}
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-gray-700">Çalışma Saatleri</p>
                    <p className="text-gray-600">Pazartesi – Cuma: 09:00 – 18:00</p>
                    <p className="text-gray-600">Cumartesi – Pazar: Kapalı</p>
                  </div>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-gray-100">
                <p className="text-sm text-gray-500 leading-relaxed">
                  Reklam veya iş birliği talebiniz için{' '}
                  <a href="/reklam-isbirligi" className="text-red-600 font-semibold hover:underline">
                    Reklam ve İş Birliği
                  </a>{' '}
                  sayfamızı ziyaret edin.
                </p>
              </div>
            </div>

            {/* Map */}
            <div className="lg:col-span-3 rounded-xl overflow-hidden border border-gray-200 bg-white">
              <iframe
                title="Ofis Konumu"
                src={`https://www.google.com/maps?q=${encodeURIComponent(ADDRESS)}&output=embed`}
                width="100%"
                height="100%"
                className="w-full h-[320px] md:h-full min-h-[360px]"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default ContactPage;
